/**
 * Normalizes text by:
 * 1. Removing accents (diacritics)
 * 2. Converting to lowercase
 * 3. Trimming whitespace
 */
export const normalizeText = (text: string): string => {
    if (!text) return '';
    return text
        .toLowerCase()
        .trim()
        .normalize('NFD') // Decompose combined characters (e.g., 'ó' -> 'o' + '´')
        .replace(/[\u0300-\u036f]/g, ''); // Remove the diacritic marks
};

/**
 * Checks if a search term is contained within a target string,
 * ignoring accents and case.
 */
export const includesNormalized = (target: string, search: string): boolean => {
    return normalizeText(target).includes(normalizeText(search));
};

/**
 * Creates a SQL-friendly search pattern that is more permissive with vowels.
 * e.g., "distribucion" -> "distr_b_c__n" or similar.
 */
export const createSearchPattern = (text: string): string => {
    if (!text) return '%';
    const normalized = text.toLowerCase().trim();
    // Replace all vowels (with or without accents) with the '_' wildcard
    // which matches exactly one character in SQL (the vowel/accented vowel)
    return `%${normalized.replace(/[aeiouáéíóú]/gi, '_')}%`;
};

/**
 * Generates a URL-friendly slug from text.
 * e.g., "iPhone 13 Pro Max" -> "iphone-13-pro-max"
 */
export const generateSlug = (text: string): string => {
    return normalizeText(text)
        .replace(/\s+/g, '-')           // Replace spaces with -
        .replace(/[^\w\-]+/g, '')       // Remove all non-word chars
        .replace(/\-\-+/g, '-')         // Replace multiple - with single -
        .replace(/^-+/, '')             // Trim - from start of text
        .replace(/-+$/, '');            // Trim - from end of text
};
